/**
 * Gate Issue Normalizer
 *
 * Converts raw gate report JSON artifacts into a canonical Issue[] shape
 * consumed by GateReportView, ResolveIssuesView, and GateStatusPanel.
 *
 * Supported gate types:
 *   'structural' — java_validation_report.json (Java Validation node)
 *   'compile'    — compile_result.json + compile_failure_snippets.json (Pre-Flight node)
 *
 * Canonical Issue shape:
 *   id            — stable string key (deterministic from file + line + code)
 *   fingerprint   — 8-char djb2 hex of 'gate_type:language:file:code:line'
 *                   Same formula used by the Python worker (decision_applier.py).
 *                   Stable across list-index changes, re-runs, and report ordering.
 *   severity      — 'BLOCK' | 'WARN'
 *   category      — error code string (e.g. "EMPTY_CLASS", "COMPILE_ERROR")
 *   summary       — human-readable one-liner
 *   recommendation — string | null
 *   fixMode       — 'user_decision_required' (no auto-fix yet for real errors)
 *   state         — 'OPEN'
 *   javaLocation  — { file: string | null, line: number | null }
 *   cobolLocation — { file: string | null, line: number | null }
 *   snippet       — string | null (code context if available from snippets file)
 *   symbolCategory — 'MISSING_TYPE' | 'MISSING_METHOD' | 'MISSING_FIELD' | 'MISSING_IMPORT' | null
 *   symbolName    — best-effort parsed symbol name, or null
 *   inferredKind  — 'class' | 'method' | 'field' | 'array' | 'unknown' | null
 *                   Set when symbolName was inferred from source_lines + column position.
 *   source_lines  — { [lineNumber: string]: string } | null
 *                   Per-error source context from compile_failure_snippets.json.
 *                   Keyed by 1-based line number as string. Used by Fix Panel / modal
 *                   to infer missing symbol at exact column position.
 *   line          — error line number (copy of javaLocation.line for convenience)
 *   column        — error column number (1-based, from errors_preview)
 */

import { inferMissingSymbol } from '../../utils/inferMissingSymbol'

/**
 * djb2 hash — stable, deterministic, no crypto API needed.
 *
 * This is the CANONICAL fingerprint implementation for the gate issue normalizer.
 * The Python worker (decision_applier.py) must use the same formula:
 *   djb2_hex(f"{gate_type}:{language}:{file}:{code}:{line}")
 *
 * Returns an 8-character lowercase hex string.
 * Exported so gateDecisionService can build fingerprints independently.
 *
 * @param {string} s
 * @returns {string} 8-char hex
 */
export function stableHash(s) {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

/**
 * Classify a Java compile error message into a missing-symbol category.
 *
 * Returns { symbolCategory, symbolName } where symbolCategory is one of:
 *   'MISSING_TYPE'   — cannot find symbol: class / interface / enum
 *   'MISSING_METHOD' — cannot find symbol: method
 *   'MISSING_FIELD'  — cannot find symbol: variable
 *   'MISSING_IMPORT' — package ... does not exist
 *   null             — not a missing-symbol error
 *
 * Maven multi-line output (symbol: class Foo\nlocation: ...) is sometimes
 * collapsed into a single message string by the errors_preview parser.
 * Both forms are handled.
 *
 * @param {string} message
 * @returns {{ symbolCategory: string|null, symbolName: string|null }}
 */
export function classifyCompileError(message) {
  const raw = message || ''
  const msg = raw.toLowerCase()

  // ── Package / import not found ────────────────────────────────────────────
  const pkgMatch = raw.match(/package\s+([\w.]+)\s+does not exist/i)
    || raw.match(/package\s+([\w.]+)\s+not found/i)
    || raw.match(/cannot find package\s+([\w.]+)/i)
  if (pkgMatch) {
    return { symbolCategory: 'MISSING_IMPORT', symbolName: pkgMatch[1] }
  }

  // ── cannot find symbol ────────────────────────────────────────────────────
  if (msg.includes('cannot find symbol')) {
    // Maven merges "symbol: class Foo" continuation lines into the message field
    // (see java_packaging_executor._parse_maven_compile_errors).
    // The merged form is: "cannot find symbol\n  symbol: class BigDecimal\n  location: ..."
    // Match both inline and newline-merged forms.
    const classMatch = raw.match(/symbol\s*:\s*class\s+(\w+)/i)
      || raw.match(/symbol\s*:\s*interface\s+(\w+)/i)
      || raw.match(/symbol\s*:\s*enum\s+(\w+)/i)
    if (classMatch) return { symbolCategory: 'MISSING_TYPE', symbolName: classMatch[1] }

    const methodMatch = raw.match(/symbol\s*:\s*method\s+(\w+)\s*\(/i)
      || raw.match(/symbol\s*:\s*method\s+(\w+)/i)
    if (methodMatch) return { symbolCategory: 'MISSING_METHOD', symbolName: methodMatch[1] }

    const varMatch = raw.match(/symbol\s*:\s*variable\s+(\w+)/i)
    if (varMatch) return { symbolCategory: 'MISSING_FIELD', symbolName: varMatch[1] }

    // Last-chance: if the summary field includes "(ClassName.java:N)" we cannot extract
    // the symbol name from it.  Leave symbolName null — the modal will prompt the user.
    return { symbolCategory: 'MISSING_TYPE', symbolName: null }
  }

  // ── class not found / cannot access ──────────────────────────────────────
  const classNotFound = raw.match(/class\s+(\w+)\s+(not found|cannot be resolved)/i)
    || raw.match(/cannot access\s+[\w.]*\.(\w+)/i)
  if (classNotFound) {
    return { symbolCategory: 'MISSING_TYPE', symbolName: classNotFound[1] || null }
  }

  return { symbolCategory: null, symbolName: null }
}

/**
 * Normalize java_validation_report.json → Issue[]
 *
 * Report schema:
 *   { files: [{ path, status, findings: [{ code, severity, message, java_line, cobol_line, cobol_file }] }] }
 *
 * @param {object} report - Parsed java_validation_report.json
 * @returns {Array} Normalized Issue[]
 */
export function normalizeJavaValidationReport(report, language = 'java') {
  const issues = []
  for (const fileResult of report.files || []) {
    const javaFilename = fileResult.path.split('/').pop()
    for (const finding of fileResult.findings || []) {
      const line = finding.java_line ?? 0
      const fp = stableHash(`structural:${language}:${javaFilename}:${finding.code}:${line}`)
      issues.push({
        id: `jval-${fileResult.path}-L${line}-${finding.code}`,
        fingerprint: fp,
        severity: finding.severity === 'ERROR' ? 'BLOCK' : 'WARN',
        category: finding.code,
        summary: `${finding.message} — ${javaFilename}`,
        recommendation: null,
        fixMode: 'user_decision_required',
        state: 'OPEN',
        javaLocation: {
          file: javaFilename,
          line: finding.java_line ?? null,
        },
        cobolLocation: {
          file: finding.cobol_file ?? null,
          line: finding.cobol_line ?? null,
        },
        snippet: null,
        symbolCategory: null,
        symbolName: null,
      })
    }
  }
  return issues
}

/**
 * Normalize compile_result.json (+ optional compile_failure_snippets.json) → Issue[]
 *
 * compile_result schema:
 *   { success, errors_preview: [{ file, line, column, message }] }
 *
 * snippets schema (optional):
 *   { files: { "rel/path/Foo.java": { source_lines: { "N": "..." }, errors: [...] } } }
 *   (compile_failure_snippets.json produced by java_packaging_executor._build_compile_snippets)
 *
 * @param {object} compileResult - Parsed compile_result.json
 * @param {object|null} snippets - Parsed compile_failure_snippets.json (optional)
 * @returns {Array} Normalized Issue[]
 */
export function normalizeCompileResult(compileResult, snippets = null, language = 'java', gatePrefix = 'compile') {
  const snippetMap = {}
  for (const s of snippets?.errors || []) {
    snippetMap[`${s.file}:${s.line}`] = s.snippet
  }

  // Build per-file source_lines map from compile_failure_snippets.json.
  // snippets.files is keyed by the relative file path (e.g. "src/main/java/…/Foo.java").
  // We index by basename for fast lookup since errors_preview uses the same relative paths.
  const fileSourceLinesMap = {}   // basename → source_lines map
  const fileSourceLinesByPath = {} // full rel path → source_lines map
  if (snippets?.files) {
    for (const [relPath, fileData] of Object.entries(snippets.files)) {
      if (fileData?.source_lines) {
        const basename = relPath.split('/').pop().split('\\').pop()
        fileSourceLinesMap[basename]      = fileData.source_lines
        fileSourceLinesByPath[relPath]    = fileData.source_lines
      }
    }
  }

  // Track occurrence count per (file, message) for stable fingerprints.
  // Maven sorts errors by file→line, so occurrence order is preserved
  // across re-runs when the same set of errors exists at shifted lines.
  const occurrenceCounters = {}

  return (compileResult.errors_preview || []).map((err) => {
    const snippetKey = `${err.file}:${err.line}`
    // Extract basename so fingerprints are stable even when Maven emits full temp paths
    // like /tmp/workspace/src/main/java/com/example/PayrollService.java.
    const javaFilename = err.file ? err.file.split('/').pop().split('\\').pop() : 'Unknown.java'
    // Fingerprint uses message content + occurrence counter, NOT line number.
    // This ensures the same error at a shifted line gets the same fingerprint,
    // allowing decisions to hydrate back onto the correct issue after re-run.
    const normalizedMsg = (err.message || '').trim().toLowerCase()
    const occKey = `${javaFilename}:${normalizedMsg}`
    const occurrence = occurrenceCounters[occKey] || 0
    occurrenceCounters[occKey] = occurrence + 1
    const fpInput = occurrence === 0
      ? `${gatePrefix}:${language}:${javaFilename}:${normalizedMsg}`
      : `${gatePrefix}:${language}:${javaFilename}:${normalizedMsg}:${occurrence}`
    const fp = stableHash(fpInput)

    let { symbolCategory, symbolName } = classifyCompileError(err.message)

    // Resolve source_lines for this error: try full path first, then basename fallback.
    const sourceLines = fileSourceLinesByPath[err.file]
      ?? fileSourceLinesMap[javaFilename]
      ?? null

    // When the message is "cannot find symbol" and Maven's detail lines were not
    // captured (symbolName is null), infer the token directly from the source text
    // at the reported column position.  This is deterministic — pure string parsing.
    let inferredKind = null
    if (!symbolName && symbolCategory && sourceLines && err.line != null && err.column != null) {
      const rawIssue = {
        line:         err.line,
        column:       err.column,
        source_lines: sourceLines,
      }
      const inferred = inferMissingSymbol(rawIssue)
      if (inferred?.token) {
        symbolName   = inferred.token
        inferredKind = inferred.kind
        // Upgrade symbolCategory based on inferred kind when we only have a generic fallback
        if (symbolCategory === 'MISSING_TYPE') {
          if (inferred.kind === 'field' || inferred.kind === 'array') symbolCategory = 'MISSING_FIELD'
          else if (inferred.kind === 'method') symbolCategory = 'MISSING_METHOD'
          // 'class' / 'unknown' → keep MISSING_TYPE
        }
      }
    }

    return {
      id: `compile-${javaFilename}-L${err.line}-${fp}`,  // stable (no idx, no full path)
      fingerprint: fp,
      severity: 'BLOCK',
      category: 'COMPILE_ERROR',
      summary: `${err.message} (${javaFilename}:${err.line})`,
      recommendation: null,
      fixMode: 'user_decision_required',
      state: 'OPEN',
      javaLocation: {
        file: javaFilename,
        line: err.line,
      },
      cobolLocation: null,
      snippet:        snippetMap[snippetKey] ?? null,
      symbolCategory: symbolCategory || null,
      symbolName:     symbolName     || null,
      inferredKind:   inferredKind   || null,
      // Thread source_lines + position through so Fix Panel / modal can re-infer
      source_lines:   sourceLines    || null,
      line:           err.line       ?? null,
      column:         err.column     ?? null,
    }
  })
}

/**
 * Derive a PASSED / WARNINGS / BLOCKED status + counts from a normalized Issue[].
 *
 * @param {Array} findings - Normalized Issue[]
 * @returns {{ status: string, counts: object }}
 */
export function deriveGateStatus(findings) {
  const blockers = findings.filter(f => f.severity === 'BLOCK').length
  const warnings = findings.filter(f => f.severity === 'WARN').length
  return {
    status: blockers > 0 ? 'BLOCKED' : warnings > 0 ? 'WARNINGS' : 'PASSED',
    counts: {
      blocking:     blockers,
      warnings:     warnings,
      autoFixable:  0,
      needsDecision: blockers,
    },
  }
}
